import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  Button,
  Box,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Block as BlockIcon, PauseCircleOutline as HoldIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import useAuth from './hooks/useAuth'; // Adjust the path based on your project structure
import checkAccess from './guards/checkNavAccess';
import { PATH_ADMIN } from './routes/paths';

const FullScreenDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: '500px',
    borderRadius: theme.shape.borderRadius * 2,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
}));

// While suspended the org keeps exactly one usable page: its own DeskOS
// billing, so it can clear the dues that caused the lockout.
const BILLING_PATH = PATH_ADMIN.deskosBilling.root;

// How often the block screen re-checks whether access has been restored (e.g.
// a colleague paid from another session, or DeskOS recorded a bank transfer).
const RECHECK_MS = 45000;

export default function SuspensionBlocker({ children }) {
  const { user, isLoading, fetchUser, hasPermission, logout } = useAuth(); // Get user from your auth hook
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isSuspended = user?.org_pay === 'suspended';

  // Not every suspension is a billing one. With no open invoice there is
  // nothing to pay (a lapsed trial, a plan that ended, a hold placed by
  // DeskOS), so the whole billing story stays hidden for those.
  const isBillingHold = isSuspended && Number(user?.org_dues || 0) > 0;

  // Same gate the /admin/deskos-billing route uses. Never offer "Pay Now" to
  // someone who would only land on a Permission Denied page.
  const canReachBilling =
    isBillingHold &&
    checkAccess({
      user,
      hasPermission,
      roles: ['org'],
      allowed: { org: ['admin', 'accountant'] },
      permission: 'settings.view',
    });

  const onBillingPage = pathname.startsWith(BILLING_PATH);
  const showBlockScreen = isSuspended && !(canReachBilling && onBillingPage);

  const recheck = useCallback(() => {
    fetchUser().catch(() => {});
  }, [fetchUser]);

  // Poll while the block screen is up so access returns on its own once the
  // dues are settled, with no re-login needed.
  useEffect(() => {
    if (!showBlockScreen) return undefined;
    const timer = setInterval(recheck, RECHECK_MS);
    window.addEventListener('focus', recheck);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', recheck);
    };
  }, [showBlockScreen, recheck]);

  // Prevent closing the dialog
  const handleClose = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      // Prevent closing
    }
  };

  // Only cover the app while we genuinely don't know who the user is yet.
  // A background refresh of an already-known user must not blank the screen.
  if (isLoading && !user) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Suspended and NOT on the billing page → block everything.
  if (showBlockScreen) {
    return (
      <FullScreenDialog
        open
        onClose={handleClose}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 1,
            backgroundColor: 'primary.main',
            color: 'white',
          }}
        >
          {isBillingHold ? <BlockIcon /> : <HoldIcon />}
          {isBillingHold ? 'Account on Billing Hold' : 'Account on Hold'}
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 2 }}>
          {isBillingHold ? (
            <>
              <Typography variant="h6" paragraph fontWeight={600}>
                Access Suspended
              </Typography>
              <Typography variant="body1" paragraph>
                We regret to inform you that access to your account and services has been temporarily suspended due
                to outstanding payment obligations.
              </Typography>
              {canReachBilling ? (
                <Typography variant="body1" paragraph>
                  You can still open your billing page and pay the outstanding invoices right here. Access for you
                  and your users is restored as soon as the payment is confirmed.
                </Typography>
              ) : (
                <Typography variant="body1" paragraph>
                  To restore full access to the platform, an admin of your workspace needs to settle the pending
                  invoices from the billing page.
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography variant="h6" paragraph fontWeight={600}>
                Your workspace is on hold
              </Typography>
              <Typography variant="body1" paragraph>
                Access to this workspace has been paused for you and your members. There are no outstanding
                invoices, so this is not a payment matter.
              </Typography>
              <Typography variant="body1" paragraph>
                Please get in touch with the DeskOS team to have access restored.
              </Typography>
            </>
          )}
          <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
            {isBillingHold ? (
              <Typography variant="body2" color="text.secondary">
                For payment assistance or inquiries, please contact our billing department at{' '}
                <a href="mailto:billing@deskos.net" style={{ color: '#1976d2', textDecoration: 'none' }}>
                  billing@deskos.net
                </a>
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                To restore access, please contact us at{' '}
                <a href="mailto:support@deskos.net" style={{ color: '#1976d2', textDecoration: 'none' }}>
                  support@deskos.net
                </a>
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            spacing={1}
            sx={{ width: '100%' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Button onClick={() => logout()} color="inherit" size="small">
              Sign out
            </Button>
            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1}>
              <Button onClick={recheck} variant="outlined" color="inherit">
                {isBillingHold ? 'Already paid? Refresh' : 'Refresh'}
              </Button>
              {canReachBilling && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(BILLING_PATH, { state: { openPay: true } })}
                  sx={{ fontWeight: 700 }}
                >
                  Pay Now
                </Button>
              )}
            </Stack>
          </Stack>
        </DialogActions>
      </FullScreenDialog>
    );
  }

  // Not suspended (or suspended and paying on the billing page): render the app.
  return <>{children}</>;
}

SuspensionBlocker.propTypes = {
  children: PropTypes.node,
};
